import {ptBR} from "@mui/material/locale";
import {createTheme, darken, lighten} from "@mui/material/styles";

export function createAppTheme(mode = "light") {
    const dark = mode === "dark";
    // Paleta compartilhada visualmente com portal-aparencia/1_0_9.
    const paper = dark ? "#202020" : "#ffffff";
    const background = dark ? "#121212" : "#f3f6f7";
    const elevated = dark ? "#292929" : "#f0f5f2";
    const text = dark ? "#ecefed" : "#233c30";
    const muted = dark ? "#b9c1bc" : "#52675b";
    const border = dark ? "#444b47" : "#b7d1bf";
    const focus = dark ? "#9bd4af" : "#358861";
    const field = dark ? "#252525" : "#f5faf6";
    const hover = dark ? "#303330" : "#edf6ef";
    const selected = dark ? "#304d39" : "#d5ebdd";
    const disabled = dark ? "#2d2d2d" : "#f0f2f1";
    const brand = "#006e3b";
    const header = {
        main: dark ? paper : brand,
        contrastText: dark ? text : "#ffffff"
    };
    const colors = {
        primary: dark ? "#9ed6b1" : brand,
        secondary: dark ? "#9ed6b1" : "#176c43",
        success: dark ? "#b5e7c7" : "#176c43",
        warning: dark ? "#f5d68b" : "#765419",
        error: dark ? "#ffb4ad" : "#a83232",
        info: dark ? "#acdced" : "#215777"
    };
    const status = {
        error: dark ? "#482727" : "#fff2f1",
        success: dark ? "#243e2e" : "#edf6ef",
        warning: dark ? "#443a21" : "#fff7df",
        info: dark ? "#193640" : "#edf5fb"
    };
    const iconAccent = {
        primary: dark ? "#4dff9a" : "#087d3e",
        secondary: dark ? "#42e8ed" : "#007c83",
        success: dark ? "#65ee88" : "#137b3a",
        warning: dark ? "#ffd247" : "#986000",
        error: dark ? "#ff7384" : "#cc3340",
        info: dark ? "#63baff" : "#096bc0"
    };
    const semantic = Object.fromEntries(
        Object.entries(colors).map(([key, main]) => [
            key,
            {
                main,
                dark: dark ? lighten(main, 0.15) : darken(main, 0.18),
                contrastText: dark ? "#121212" : "#ffffff"
            }
        ])
    );
    return createTheme(
        {
            palette: {
                mode,
                ...semantic,
                brand: {main: brand, contrastText: "#ffffff"},
                header,
                iconAccent,
                // Ícones têm acentos mais vivos que os textos da paleta do Portal.
                icon: {
                    primary: dark ? "#4dff9a" : "#ffffff",
                    secondary: dark ? "#42e8ed" : "#ffffff",
                    success: dark ? "#65ee88" : "#ffffff",
                    warning: dark ? "#ffd247" : "#6f4600",
                    error: dark ? "#ff7384" : "#ffffff",
                    info: dark ? "#63baff" : "#ffffff"
                },
                iconBackground: {
                    primary: dark ? "#0e402a" : "#087d3e",
                    secondary: dark ? "#123c40" : "#007c83",
                    success: dark ? "#163e24" : "#137b3a",
                    warning: dark ? "#4b3707" : "#ffdc73",
                    error: dark ? "#4c1d28" : "#cc3340",
                    info: dark ? "#123855" : "#096bc0"
                },
                white: "#ffffff",
                black: "#000000",
                background: {default: background, paper},
                text: {
                    primary: text,
                    secondary: muted
                },
                divider: border,
                surface: {subtle: elevated},
                status,
                action: {
                    active: muted,
                    hover,
                    selected,
                    disabled: muted,
                    disabledBackground: disabled
                }
            },
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        html: {
                            colorScheme: mode,
                            overflowY: "auto",
                            scrollbarGutter: "stable",
                            scrollbarWidth: "auto",
                            scrollbarColor: dark
                                ? "#b9c1bc #202020"
                                : "#64748b #e5e7eb"
                        },
                        ":focus-visible": {
                            outline: `2px solid ${focus}`,
                            outlineOffset: 3
                        },
                        ".recharts-cartesian-axis-tick-value": {
                            fill: muted
                        },
                        "@media print": {
                            "html, body": {
                                colorScheme: "light",
                                backgroundColor: "#ffffff !important",
                                color: "#233c30 !important"
                            },
                            ".MuiPaper-root, .MuiTableCell-root, .MuiAlert-root, .MuiChip-root":
                                {
                                    backgroundColor: "#ffffff !important",
                                    color: "#233c30 !important",
                                    boxShadow: "none !important"
                                },
                            ".MuiTypography-root, .MuiInputBase-root, .MuiFormLabel-root, .MuiFormHelperText-root, .MuiListItemText-root":
                                {color: "#233c30 !important"},
                            ".MuiOutlinedInput-notchedOutline": {
                                borderColor: "#738478 !important"
                            },
                            ".recharts-text": {fill: "#233c30 !important"}
                        }
                    }
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            backgroundColor: header.main,
                            color: header.contrastText,
                            backgroundImage: "none"
                        }
                    }
                },
                MuiPaper: {styleOverrides: {root: {backgroundImage: "none"}}},
                MuiIcon: {
                    styleOverrides: {
                        root: ({ownerState}) => ({
                            color: iconAccent[ownerState.color]
                        })
                    }
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: ({ownerState}) => ({
                            "&:not(.Mui-disabled)": {
                                color: iconAccent[ownerState.color]
                            }
                        })
                    }
                },
                MuiTab: {
                    styleOverrides: {
                        root: {
                            "&.Mui-selected .MuiIcon-root": {
                                color: iconAccent.primary
                            }
                        }
                    }
                },
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            backgroundColor: field,
                            "&:hover": {backgroundColor: hover},
                            "&.Mui-focused": {
                                backgroundColor: dark ? "#29362e" : "#e1efe5"
                            },
                            "&.Mui-disabled": {backgroundColor: disabled},
                            "&.Mui-error": {backgroundColor: status.error}
                        },
                        notchedOutline: {
                            borderColor: border
                        }
                    }
                },
                MuiTableCell: {
                    styleOverrides: {
                        head: {
                            backgroundColor: elevated,
                            color: text,
                            fontWeight: 700
                        }
                    }
                },
                MuiButton: {
                    styleOverrides: {
                        root: ({ownerState}) =>
                            ownerState.variant !== "contained"
                                ? {
                                      "&:not(.Mui-disabled) .MuiButton-startIcon > .MuiIcon-root, &:not(.Mui-disabled) .MuiButton-endIcon > .MuiIcon-root":
                                          {color: iconAccent[ownerState.color]}
                                  }
                                : {},
                        containedPrimary: {
                            backgroundColor: brand,
                            color: "#ffffff",
                            "&:hover": {backgroundColor: "#185e38"},
                            "&.Mui-disabled": {
                                backgroundColor: disabled,
                                color: muted
                            }
                        }
                    }
                },
                MuiAlert: {
                    styleOverrides: Object.fromEntries(
                        Object.entries(status).map(
                            ([name, backgroundColor]) => [
                                `standard${name[0].toUpperCase()}${name.slice(
                                    1
                                )}`,
                                {
                                    backgroundColor,
                                    color: colors[name],
                                    "& .MuiAlert-icon": {
                                        color: iconAccent[name],
                                        opacity: 1
                                    }
                                }
                            ]
                        )
                    )
                }
            },
            mixins: {toolbar: {minHeight: 40}}
        },
        ptBR
    );
}

export default createAppTheme();
